<template>
	<r-standard-modal-form 
		:style="{
			height: `642px`,
		}"
		themeSize="no-padding"
		:containerWidth="740"
		:showCloseX="false"
		:showHeaderRow="false"
		:closeOnEscapePress="!currentTabFlow"
		@closeModal="$emit('closeModal')"
	>
		<div slot="body" class="configurator-container-body">
			<div 
				v-if="!fixProblem && !currentTabFlow"
				class="configurator-x-button-container" 
			>
				<r-standard-icon 
					icon="close"
					color="grey-300"
					class="configurator-x-button"
					@click="$emit('closeModal')"
				>	
				</r-standard-icon>
			</div>
			<div class="configurator-grid">
				<div class="configurator-left-column">
					<div
						class="configurator-title"
					>
						<span class="title-sheet-type">
							{{ sheetViewTypeLabel }} View
						</span>
						<span class="title-divider"></span>
						<span class="title-setup">
							{{ setupTypeLabel }}
						</span>
					</div>
					<div
						class="configurator-tab-container"
					>
						<r-tip-hover
							v-for="(tabDetails, tabIndex) in sortedSheetViewConfigurationTabs"
							:key="tabDetails.tabKey"
							class="configurator-help-message-tip-hover"
							position="bottom"
							:text="invalidContinueMessage"
							:disabled="(tabDetails.tabKey === 'plottedData' || sheetViewIsValid)"
							:relativeAlignmentActivator="0.6"
							:relativeAlignmentContainer="0.35"
							contentBackgroundColor="#6A6B76"
							:openOnHoverDelay="10"
						>
							<div
								class="configurator-tab"
								:class="{
									'selected': (selectedTab === tabDetails.tabKey),
									'warning': (tabDetails.tabKey === 'plottedData' && !sheetViewIsValid),
									'disabled': (tabDetails.tabKey !== 'plottedData' && !sheetViewIsValid),
								}"
								@click="setSelectedTab(tabDetails.tabKey)"
							>
								<r-standard-icon
									class="configurator-tab-icon"
									:icon="tabDetails.icon"
									:originalSize="true"
									:size="{
										height: 20,
										width: 20,
									}"
									:hexColor="getTabIconColor(tabDetails)"
								></r-standard-icon>
								<div class="configurator-tab-text">
									{{ tabDetails.label }}
								</div>
							</div>
						</r-tip-hover>
					</div>
					<div
						class="configurator-component-container"
					>
						<component
							:is="currentTabComponent"
							:sheetView="sheetView"
							:tabFlow="currentTabFlow"
							@closeModal="$emit('closeModal')"
						>
						</component>
					</div>
					<div
						class="configurator-footer"
					>
						<r-pop-menu
							class="configurator-objects-status-pop"
							relative="top"
							:openOnHover="false"
							:openOnHoverDelay="100"
							:closeOnClick="false"
							:closeOnContentClick="false"
							:relativeAlignmentActivator="0"
							:relativeAlignmentContainer="0.035"
							:spaceBetweenActivatorAndContent="0"
						>
							<div
								slot="activator"
								class="configurator-objects-status-container"
							>
								<r-standard-icon
									class="configurator-objects-status-icon"
									:class="{
										'warning': objectStatusWarning,
										'deactivated': objectStatusDeactive,
									}"
									:icon="objectStatusIcon"
									:size="{
										height: 24,
										width: 24,
									}"
									:hexColor="statusIconColor"
								></r-standard-icon>
								<div
									class="configurator-objects-status-text"
									:class="{
										'warning': objectStatusWarning,
										'deactivated': objectStatusDeactive,
									}"
								>
									{{ objectStatusText }}
								</div>
							</div>
							<div
								slot="content"
								class="configurator-objects-status-content"
							>
								<div 
									class="objects-status-title"
									:class="{
										'warning': objectStatusWarning
									}"
								>
									{{ objectStatusPopText }}
								</div>
								<div class="objects-status-body">
									<div
										class="objects-status-group no-records"
										v-if="dataStoreObjects.length === 0"
									>
										<r-standard-icon
											class="objects-status-group-icon"
											icon="plottedData"
											:size="{
												height: 20,
												width: 20,
											}"
											hexColor="#9CA1AB"
										></r-standard-icon>
										<div
											class="objects-status-group-content"
										>
											<div class="objects-status-group-content-header">
												You have no records to plot in your workbook yet
											</div>
											<div class="group-content-info-container">
												<r-standard-icon
													class="group-content-info-icon"
													icon="info"
													:size="{
														height: 18,
														width: 18,
													}"
												></r-standard-icon>
												<div class="group-content-info-text">
													We recommend adding (or importing) your records by going to a 'Table' view below.
												</div>
											</div>
										</div>
									</div>
									<div
										class="objects-status-group missing"
										v-else-if="Object.keys(missingFields).length > 0"
									>
										<r-standard-icon
											class="objects-status-group-icon"
											icon="plottedData"
											:size="{
												height: 20,
												width: 20,
											}"
											hexColor="#9CA1AB"
										></r-standard-icon>
										<div
											class="objects-status-group-content"
										>
											<div class="objects-status-group-content-header">
												You must select {{ Object.keys(missingFields).length }} more data field{{ Object.keys(missingFields).length>1?'s':''}} to plot on this {{ sheetViewTypeLabel }}:
											</div>
											<div 
												v-if="missingFields.groupMissing"
												class="objects-status-group-content-detail"
											>
												<div class="status-group-content-field-info fullwidth">
													The Swimlane field is missing
												</div>
											</div>
											<div 
												v-if="missingFields.startMissing"
												class="objects-status-group-content-detail"
											>
												<div class="status-group-content-field-info fullwidth">
													Start Date field is missing
												</div>
											</div>
											<div 
												v-if="missingFields.endMissing"
												class="objects-status-group-content-detail"
											>
												<div class="status-group-content-field-info fullwidth">
													End Date field is missing
												</div>
											</div>
											<div class="group-content-info-container">
												<r-standard-icon
													class="group-content-info-icon"
													icon="info"
													:size="{
														height: 18,
														width: 18,
													}"
												></r-standard-icon>
												<div class="group-content-info-text">
													Please select fields in the 'Plotted Data' tab.
												</div>
											</div>
										</div>
									</div>
									<div
										class="objects-status-group missing"
										v-else-if="objectStatusCategoryCounts.totalMissing > 0"
									>
										<r-standard-icon
											class="objects-status-group-icon"
											icon="plottedData"
											:size="{
												height: 20,
												width: 20,
											}"
											hexColor="#9CA1AB"
										></r-standard-icon>
										<div
											class="objects-status-group-content"
										>
											<div class="objects-status-group-content-header">
												{{ objectStatusCategoryCounts.totalMissing }} of your records {{(objectStatusCategoryCounts.totalMissing>1?'have':'has')}} missing values in the selected fields:
											</div>
											<span 
												v-if="objectStatusCategoryCounts.groupMissing && objectStatusCategoryCounts.groupMissing > 0"
												class="objects-status-group-content-detail"
											>
												'
												<universal-field-label-display
													v-if="proposedSheetViewPlotParameters.groupByField !== null"
													class="status-group-content-field-label"
													:dataStore="dataStore"
													:fieldGUID="proposedSheetViewPlotParameters.groupByField"
													:inline="true"
												></universal-field-label-display>
												<span class="status-group-content-field-info">
													'&nbsp;&nbsp;is missing for {{ objectStatusCategoryCounts.groupMissing }}/{{ dataStoreObjects.length }} records
												</span>
											</span>
											<span 
												v-if="objectStatusCategoryCounts.startMissing > 0"
												class="objects-status-group-content-detail"
											>
												'
												<universal-field-label-display
													v-if="proposedSheetViewPlotParameters.startDateField !== null"
													class="status-group-content-field-label"
													:dataStore="dataStore"
													:fieldGUID="proposedSheetViewPlotParameters.startDateField"
													:inline="true"
												></universal-field-label-display>
												<span class="status-group-content-field-info">
													'&nbsp;&nbsp;is missing for {{ objectStatusCategoryCounts.startMissing }}/{{ dataStoreObjects.length }} records
												</span>
											</span>
											<span 
												v-if="objectStatusCategoryCounts.endMissing > 0"
												class="objects-status-group-content-detail"
											>
												'
												<universal-field-label-display
													v-if="proposedSheetViewPlotParameters.endDateField !== null"
													class="status-group-content-field-label"
													:dataStore="dataStore"
													:fieldGUID="proposedSheetViewPlotParameters.endDateField"
													:inline="true"
												></universal-field-label-display>
												<span class="status-group-content-field-info">
													'&nbsp;&nbsp;is missing for {{ objectStatusCategoryCounts.endMissing }}/{{ dataStoreObjects.length }} records
												</span>
											</span>
											<div class="group-content-info-container">
												<r-standard-icon
													class="group-content-info-icon"
													icon="info"
													:size="{
														height: 18,
														width: 18,
													}"
												></r-standard-icon>
												<div class="group-content-info-text">
													Add the missing values by going to a 'Table' view below.
												</div>
											</div>
										</div>
									</div>
									<div
										class="objects-status-group date-order"
										v-if="objectStatusCategoryCounts.dateOrder > 0"
									>
										<r-standard-icon
											class="objects-status-group-icon"
											icon="warnExclamation"
											:size="{
												height: 20,
												width: 20,
											}"
											hexColor="#9CA1AB"
										></r-standard-icon>
										<div
											class="objects-status-group-content"
										>
											<span class="objects-status-group-content-header">
												{{ objectStatusCategoryCounts.dateOrder }} of your records {{(objectStatusCategoryCounts.dateOrder>1?'have':'has')}} values for&nbsp;&nbsp;'
												<universal-field-label-display
													v-if="proposedSheetViewPlotParameters.endDateField !== null"
													class="status-group-content-field-label"
													:dataStore="dataStore"
													:fieldGUID="proposedSheetViewPlotParameters.endDateField"
													:inline="true"
												></universal-field-label-display>
												'&nbsp;&nbsp;that {{(objectStatusCategoryCounts.dateOrder>1?'are':'is')}} earlier than their value for&nbsp;&nbsp;'
												<universal-field-label-display
													v-if="proposedSheetViewPlotParameters.startDateField !== null"
													class="status-group-content-field-label"
													:dataStore="dataStore"
													:fieldGUID="proposedSheetViewPlotParameters.startDateField"
													:inline="true"
												></universal-field-label-display>
												'
											</span>
											<div class="group-content-info-container">
												<r-standard-icon
													class="group-content-info-icon"
													icon="info"
													:size="{
														height: 18,
														width: 18,
													}"
												></r-standard-icon>
												<div class="group-content-info-text">
													To display the remaining {{ objectStatusCategoryCounts.dateOrder }} record{{(objectStatusCategoryCounts.dateOrder>1?'s':'')}}, please correct the date values by going to a 'Table' view below.
												</div>
											</div>
										</div>
									</div>
									<div
										class="objects-status-group nested-level"
										v-if="objectStatusCategoryCounts.recordsInLevels < dataStoreObjects.length"
									>
										<r-standard-icon
											class="objects-status-group-icon"
											icon="levelsShown"
											:size="{
												height: 20,
												width: 20,
											}"
											hexColor="#9CA1AB"
										></r-standard-icon>
										<div
											class="objects-status-group-content"
										>
											<span class="objects-status-group-content-header">
												You've selected to display {{ nestingInfo.selectedLevels }} of {{ nestingInfo.totalLevels }} nested levels, and this selection has {{ objectStatusCategoryCounts.recordsInLevels }} displayable records.
											</span>
											<div class="group-content-info-container">
												<r-standard-icon
													class="group-content-info-icon"
													icon="info"
													:size="{
														height: 18,
														width: 18,
													}"
												></r-standard-icon>
												<div class="group-content-info-text">
													To display more nested levels, select them in the 'Levels Shown' tab.
												</div>
											</div>
										</div>
									</div>

								</div>
							</div>
						</r-pop-menu>
						<r-tip-hover
							class="configurator-help-message-tip-hover"
							position="top"
							:text="invalidContinueMessage"
							:disabled="currentTabIsValid && sheetViewIsValid"
							:relativeAlignmentActivator="0.6"
							:relativeAlignmentContainer="0.8"
							contentBackgroundColor="#6A6B76"
							:openOnHoverDelay="10"
						>
							<r-button
								class="configurator-submit-button"
								theme="dark-blue" 
								outline
								:enabled="currentTabIsValid && sheetViewIsValid"
								@click="clickSubmit"
							>
								{{ submitButtonText }}
							</r-button>
						</r-tip-hover>
					</div>
				</div>
				<div 
					class="configurator-right-bar"
					:class="{
						'tab-flow': currentTabFlow,
					}"
				>
					<div 
						v-if="sheetViewTypePreviewMap.header !== null"
						class="preview-container"
					>
						<div class="preview-header">
							{{ sheetViewTypePreviewMap.header }}
						</div>
						<div class="preview-image">
							<img class="icon-item" :src="sheetViewTypePreviewMap.image" alt="Preview"/>
						</div>
						<div class="preview-feature-conatiner">
							<div class="feature-item"
								v-for="featureText, featureIndex in sheetViewTypePreviewMap.featureList"
							>
								<svg class="feature-check" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M15.4989 5.11488L7.44201 13.1718L4.50109 10.2002C4.34792 10.0777 4.10284 10.0777 3.98031 10.2002L3.0919 11.0886C2.96937 11.2112 2.96937 11.4562 3.0919 11.6094L7.19694 15.6838C7.35011 15.837 7.56455 15.837 7.71772 15.6838L16.9081 6.49344C17.0306 6.3709 17.0306 6.12582 16.9081 5.97265L16.0197 5.11488C15.8972 4.96171 15.6521 4.96171 15.4989 5.11488Z" fill="#00B2BD"/>
								</svg>
								<div class="feature-text">
									{{ featureText }}
								</div>
							</div>
						</div>
					</div>
					<div class="testimonial-container">
						<div class="testimonial-header">
							Planning and communicating are about to get way easier.
						</div>
						<div class="testimonial-quote">
							"Visor's drag & drop views bring my plans to life. I'm getting hours of my time back by using Visor's sync."
						</div>
						<div class="testimonial-attribution">
							<div class="attribution-title">
								Senior Director of Innovation
							</div>
							<div class="attribution-company">
								at Global Financial Firm
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</r-standard-modal-form>
</template>

<script>

import SheetViewTypes from 'js/sheet-view/sheet-view-types';

import UniversalFieldLabelDisplay from 'vue/sheet-views/common/fields/universal-field-label-display.vue';

export default {
	$pubsub:{
		configuratorModal:{
			subscriptions:{
				changeSheetValidity({sheetIsValid}){
					this.currentTabIsValid = sheetIsValid;
				},
				updateProposedConfiguration(proposedConfiguration){
					this.currentProposedConfiguration = proposedConfiguration;
				},
			}
		},
	},
	components:{
		UniversalFieldLabelDisplay,
	},
	data(){
		return {
			selectedTab: null,
			currentTabFlow: true,
			currentTabIsValid: true,
			currentProposedConfiguration: null,
		};
	},
	inject: ['sheetGlobalState', 'fieldCache'],
	props:{
		sheetView:{
			required: true,
			type: Object,
		},
		initialTab:{
			required: false,
			type: String,
			default: null,
		},
		tabFlow:{
			required: false,
			type: Boolean,
			default: true,
		},
		fixProblem:{
			required: false,
			type: Boolean,
			default: false,
		},
	},
	mounted(){
		if(this.initialTab){
			this.selectedTab = this.initialTab;
		}
		else{
			this.selectedTab = this.sortedSheetViewConfigurationTabs[0].tabKey;
		}
		let sheetViewConfig = this.sheetView.getOrCreateDisplayConfigurator();

		this.currentTabFlow = this.tabFlow;
		if(this.sheetViewType !== 'table' && sheetViewConfig.dateLastConfigured === null){
			this.currentTabFlow = true;
		}
	},
	watch:{
		selectedTab(){
			this.currentProposedConfiguration = null;
		}
	},
	computed:{
		sheetViewType(){
			return this.sheetView.type;
		},
		dataStore(){
			return this.sheetView.sheetViewGroup.dataStore;
		},
		schema(){
			return this.dataStore.meta.schema;
		},
		sheetViewTypeLabel(){
			return SheetViewTypes.types[this.sheetViewType].label;
		},
		titleText(){
			return `How would you like to configure this ${this.sheetViewTypeLabel} View?`;
		},
		setupTypeLabel(){
			if(this.currentTabFlow){
				return "Setup";
			}
			return "Edit";
		},
		sheetViewTypePreviewMap(){
			if(this.sheetViewType === 'gantt'){
				return {
					header: `You're just seconds away from a beautiful, interactive gantt chart.`,
					image: require('assets/configurator/gantt.png'),
					featureList: [
						'Drag & drop',
						'Conditional formatting',
						'Two-way sync with Jira',
					],
				};
			}
			else if(this.sheetViewType === 'timeline'){
				return {
					header: `You're just seconds away from a beautiful, interactive timeline chart.`,
					image: require('assets/configurator/timeline.png'),
					featureList: [
						'Drag & drop',
						'Conditional formatting',
						'Two-way sync with Jira',
					],
				};
			}
			return {
				header: null,
				image: null,
				featureList: [],
			};
		},
		dataStoreObjects(){
			return this.dataStore.getRecords();
		},
		objectStatusCount(){
			return this.sheetPlotStatus.shownCount;
		},
		objectStatusText(){
			if(this.sheetViewType === 'table'){
				return `${this.objectStatusCount} of ${this.dataStoreObjects.length} rows displayed`;
			}
			else{
				return `${this.objectStatusCount} of ${this.dataStoreObjects.length} objects plotted`;
			}
		},
		objectStatusIcon(){
			if(this.objectStatusCount <= 0){
				return "plottedSlash";
			}
			return "plottedIcon";
			// return "questionMarkBlock";
		},
		statusIconColor(){
			if(this.objectStatusDeactive){
				return '#9CA1AB';
			}
			else if(this.objectStatusWarning){
				return '#FF7A30';
			}
			else{
				return '#0086FF';
			}
		},
		objectStatusPopText(){
			if(this.dataStoreObjects.length === 0){
				if(this.sheetViewType === 'table'){
					return `Why are 0 rows displaying here?`;
				}
				return `Why are 0 objects plotting here?`;
			}

			if(this.objectStatusCount === this.dataStoreObjects.length){
				return `Nice job! You plotted all records in this Workbook.`;
			}

			if(this.sheetViewType === 'table'){

				return `Why ${(this.objectStatusCount===1?'is':'are')} ${(this.objectStatusCount===0?'':'only')} ${this.objectStatusCount} of ${this.dataStoreObjects.length} rows displaying here?`;
			}
			else{
				return `Why ${(this.objectStatusCount===1?'is':'are')} ${(this.objectStatusCount===0?'':'only')} ${this.objectStatusCount} of ${this.dataStoreObjects.length} objects plotting here?`;
			}
		},
		missingFields(){
			const schema = this.schema;
			const proposedParams = this.proposedSheetViewPlotParameters;
			let returnMissingFields = {};

			if(this.sheetViewType === 'timeline'){
				if(!proposedParams.startDateField || !schema.fields[proposedParams.startDateField]){
					returnMissingFields.startMissing = true;
				}
				if(!proposedParams.endDateField || !schema.fields[proposedParams.endDateField]){
					returnMissingFields.endMissing = true;
				}
				if(!proposedParams.groupByField || !schema.fields[proposedParams.groupByField]){
					returnMissingFields.groupMissing = true;
				}
			}
			else if(this.sheetViewType === 'gantt'){
				if(!proposedParams.startDateField || !schema.fields[proposedParams.startDateField]){
					returnMissingFields.startMissing = true;
				}
				if(!proposedParams.endDateField || !schema.fields[proposedParams.endDateField]){
					returnMissingFields.endMissing = true;
				}
			}

			return returnMissingFields;
		},
		nestingInfo(){
			const vcSelf = this;
			if(vcSelf.sheetViewType === 'timeline'){
				if(vcSelf.proposedSheetViewPlotParameters.nestedFilterField !== null && vcSelf.dataStore.meta.schema.fields[vcSelf.proposedSheetViewPlotParameters.nestedFilterField] && vcSelf.dataStore.meta.schema.fields[vcSelf.proposedSheetViewPlotParameters.nestedFilterField].field.fieldType !== 'internalEntityLink'){
					const levelOptions = vcSelf.fieldCache.picklistValues[vcSelf.proposedSheetViewPlotParameters.nestedFilterField];
					return {
						totalLevels: levelOptions.length,
						selectedLevels: vcSelf.proposedSheetViewPlotParameters.nestedFilterSelections.length,
					};
				}
				else{
					return {
						totalLevels: vcSelf.sheetGlobalState.dataStoreRecordsByLevels.length,
						selectedLevels: 1,
					};
				}
			}
			else{
				return {
					totalLevels: vcSelf.sheetGlobalState.dataStoreRecordsByLevels.length,
					selectedLevels: vcSelf.proposedSheetViewPlotParameters.nestedDepth + 1,
				};
			}
		},
		objectStatusCategoryCounts(){
			let returnCategoryCounts = {};
			if(this.sheetViewType === 'timeline'){
				returnCategoryCounts = {
					'startMissing': 0,
					'endMissing': 0,
					'groupMissing': 0,
					'totalMissing': 0,
					'dateOrder': 0,
					'recordsInLevels': 0,
				}
			}
			else if(this.sheetViewType === 'gantt'){
				returnCategoryCounts = {
					'startMissing': 0,
					'endMissing': 0,
					'totalMissing': 0,
					'dateOrder': 0,
					'recordsInLevels': 0,
				}
			}
			else{
				returnCategoryCounts = {
					'recordsInLevels': 0,
				}
			}

			const sheetType = this.sheetViewType;
			let currentSheetPlotStatus;
			let hadMissingValue;

			for(let statusFieldGUID in this.sheetPlotStatus.explanations){
				currentSheetPlotStatus = this.sheetPlotStatus.explanations[statusFieldGUID];
				hadMissingValue = false;
				if(sheetType === 'timeline'){
					if(!currentSheetPlotStatus.startDateRecordValue){
						hadMissingValue = true;
						returnCategoryCounts.startMissing += 1;
					}

					if(!currentSheetPlotStatus.endDateRecordValue){
						hadMissingValue = true;
						returnCategoryCounts.endMissing += 1;
					}

					if(!currentSheetPlotStatus.groupByRecordValue){
						hadMissingValue = true;
						returnCategoryCounts.groupMissing += 1;
					}

					if(hadMissingValue){
						returnCategoryCounts.totalMissing += 1;
					}

					if(!currentSheetPlotStatus.dateOrder){
						returnCategoryCounts.dateOrder += 1;
					}

					if(currentSheetPlotStatus.includesLevel){
						returnCategoryCounts.recordsInLevels += 1;
					}
				}
				else if(sheetType === 'gantt'){
					if(!currentSheetPlotStatus.startDateRecordValue){
						hadMissingValue = true;
						returnCategoryCounts.startMissing += 1;
					}

					if(!currentSheetPlotStatus.endDateRecordValue){
						hadMissingValue = true;
						returnCategoryCounts.endMissing += 1;
					}

					if(hadMissingValue){
						returnCategoryCounts.totalMissing += 1;
					}

					if(!currentSheetPlotStatus.dateOrder){
						returnCategoryCounts.dateOrder += 1;
					}

					if(currentSheetPlotStatus.includesLevel){
						returnCategoryCounts.recordsInLevels += 1;
					}
				}
				else{
					if(currentSheetPlotStatus.includesLevel){
						returnCategoryCounts.recordsInLevels += 1;
					}
				}
			}

			return returnCategoryCounts;
		},
		objectStatusWarning(){
			return this.objectStatusCount <= 0;
		},
		objectStatusDeactive(){
			return !this.sheetViewIsValid;
		},
		currentTabIndex(){
			for(let tabIndex=0; tabIndex<this.sortedSheetViewConfigurationTabs.length; tabIndex++){
				if(this.sortedSheetViewConfigurationTabs[tabIndex].tabKey === this.selectedTab){
					return tabIndex;
				}
			}
			return -1;
		},
		submitButtonText(){
			if(this.currentTabFlow){
				if(this.currentTabIndex === this.sortedSheetViewConfigurationTabs.length-1){
					return "Finish Setup";
				}
				else{
					return "Continue";
				}
			}
			return "Done";
		},
		sortedSheetViewConfigurationTabs(){
			const vcSelf = this;
			return Object.keys(SheetViewTypes.types[vcSelf.sheetView.type].configurator.pages).map(function generatePageArray(pageKey){
				return Object.assign({}, {tabKey: pageKey}, SheetViewTypes.types[vcSelf.sheetView.type].configurator.pages[pageKey]);
			}).sort(function sortByOrder(pageStructA, pageStructB){
				return pageStructA.sortOrder - pageStructB.sortOrder;
			});
		},
		currentTabComponent(){
			if(this.selectedTab){
				return SheetViewTypes.types[this.sheetView.type].configurator.pages[this.selectedTab].templateComponent;
			}
		},
		sheetViewPlotParameters(){
			return SheetViewTypes.types[this.sheetView.type].plotted.getSheetViewPlotParameters({
				sheetView: this.sheetView, 
				SystemState: this.$root.systemState, 
				ConnectedApps: this.sheetGlobalState.ConnectedApps,
			});
		},
		proposedSheetViewPlotParameters(){
			if(this.currentProposedConfiguration){
				return Object.assign(this.sheetViewPlotParameters, this.currentProposedConfiguration);
			}
			else{
				return this.sheetViewPlotParameters;
			}
		},
		sheetPlotStatus(){
			const statusByRecordGUID = {
				shownCount: 0,
				explanations: {},
			};
			const currentPlotParameters = this.proposedSheetViewPlotParameters;
			let currentRecord = null;
			let recordGUID = null;
			for(let recordIndex=0; recordIndex < this.dataStoreObjects.length; recordIndex++){
				currentRecord = this.dataStoreObjects[recordIndex];
				recordGUID = currentRecord.$internals.metaProperties.meta.guid;
				statusByRecordGUID.explanations[recordGUID] = SheetViewTypes.types[this.sheetView.type].plotted.getPlotStatusForRecord({
					record: currentRecord,
					sheetView: this.sheetView,
					plotParameters: currentPlotParameters,
					dataStoreRecordsByLevels: this.sheetGlobalState.dataStoreRecordsByLevels,
					picklistController: this.sheetGlobalState.picklistController,
					fieldCache: this.fieldCache,
					SystemState: this.$root.systemState, 
					ConnectedApps: this.sheetGlobalState.ConnectedApps,
				})

				if(statusByRecordGUID.explanations[recordGUID].isPlotted){
					statusByRecordGUID.shownCount += 1;
				}
			}
			return statusByRecordGUID;
		},
		sheetViewIsValid(){
			const schema = this.dataStore.meta.schema;
			const sheetView = this.sheetView;
			const plotParameters = this.proposedSheetViewPlotParameters;

			if(this.sheetViewType === 'timeline'){
				const timelineStartGUID = plotParameters.startDateField;
				const timelineEndGUID = plotParameters.endDateField;
				const timelineGroupGUID = plotParameters.groupByField;

				let timelineValidObject = {
					isTimeline: sheetView.type === "timeline",
					startDateField: (timelineStartGUID !== null) && !!(schema.fields[timelineStartGUID]),
					endDateField: (timelineEndGUID !== null) && !!(schema.fields[timelineEndGUID]),
					groupByField: (timelineGroupGUID !== null) && !!(schema.fields[timelineGroupGUID]),
				}
				timelineValidObject.isValid = (
					timelineValidObject.isTimeline && 
					timelineValidObject.startDateField &&
					timelineValidObject.endDateField &&
					timelineValidObject.groupByField
				);
				return timelineValidObject.isValid;
			}
			else if(this.sheetViewType === 'gantt'){
				const timelineStartGUID = plotParameters.startDateField;
				const timelineEndGUID = plotParameters.endDateField;

				let ganttValidObject = {
					isGantt: sheetView.type === "gantt",
					startDateField: (timelineStartGUID !== null) && !!(schema.fields[timelineStartGUID]),
					endDateField: (timelineEndGUID !== null) && !!(schema.fields[timelineEndGUID]),
				}
				ganttValidObject.isValid = (
					ganttValidObject.isGantt && 
					ganttValidObject.startDateField &&
					ganttValidObject.endDateField
				);
				return ganttValidObject.isValid;
			}
			return true;
		},
		sheetViewInvalidFields(){
			let invalidFields = [];
			const schema = this.schema;
			const plotParameters = this.proposedSheetViewPlotParameters;
			if(this.sheetViewType === 'timeline'){
				const timelineStartGUID = plotParameters.startDateField;
				const timelineEndGUID = plotParameters.endDateField;
				const timelineGroupGUID = plotParameters.groupByField;

				if((timelineGroupGUID === null) || !(schema.fields[timelineGroupGUID])){
					invalidFields.push("Swimlane");
				}
				if((timelineStartGUID === null) || !(schema.fields[timelineStartGUID])){
					invalidFields.push("Start Date");
				}
				if((timelineEndGUID === null) || !(schema.fields[timelineEndGUID])){
					invalidFields.push("End Date");
				}
			}
			else if(this.sheetViewType === 'gantt'){
				const ganttStartGUID = plotParameters.startDateField;
				const ganttEndGUID = plotParameters.endDateField;

				if((ganttStartGUID === null) || !(schema.fields[ganttStartGUID])){
					invalidFields.push("Start Date");
				}
				if((ganttEndGUID === null) || !(schema.fields[ganttEndGUID])){
					invalidFields.push("End Date");
				}
			}
			return invalidFields;
		},
		invalidContinueMessage(){
			if(this.sheetViewInvalidFields){
				const invalidFieldsArray = this.sheetViewInvalidFields;

				if(this.sheetViewInvalidFields.length === 1){
					if(this.sheetViewInvalidFields[0] === 'End Date'){
						// I hate english 
						return `Pick an ${this.sheetViewInvalidFields[0]} Field on this page before proceeding.`
					}
					else{
						return `Pick a ${this.sheetViewInvalidFields[0]} Field on this page before proceeding.`
					}
				}
				else{
					return `Pick ${[invalidFieldsArray.slice(0, -1).join(', '), invalidFieldsArray.slice(-1)[0]].join(invalidFieldsArray.length < 2 ? '' : ' and ')} Fields on this page before proceeding.`
				}
			}
			return '';
		},
	},
	methods:{
		setSelectedTab(tabKey){
			const vcSelf = this;
			if(vcSelf.selectedTab !== tabKey){
				if(tabKey !== 'plottedData' && !vcSelf.sheetViewIsValid){
					// dont navigate away from the plottedData page
					return;
				}

				vcSelf.$pubsub.modules.configuratorModal.pub('submitClicked', {
					pageKey: vcSelf.selectedTab,
				});
				vcSelf.notifiyTabSaved(vcSelf.selectedTab);
				vcSelf.selectedTab = tabKey;
			}

			return;
		},
		notifiyTabSaved(tabKey){
			const vcSelf = this;
			const selectedTabLabel = vcSelf.sortedSheetViewConfigurationTabs.find(function matchesPage(pageDetails){
				return pageDetails.tabKey === tabKey;
			}).label;

			vcSelf.$pubsub.modules.toaster.pub("makeToast", {
				message: [{
					label: "Saved",
					props: {
						class: "p-typography-regular-14 p-typography-inline"
					}
				}, {
					label: selectedTabLabel,
					props: {
						class: "p-typography-bold-14 p-typography-inline"
					}
				}],
				type: "standard",
				location: "bottom-left",
				duration: 2000,
			});
		},
		getTabIconColor(tabDetails){
			if(this.selectedTab === tabDetails.tabKey){
				return '#0B4880';
			}
			else if(tabDetails.tabKey === 'plottedData' && !this.sheetViewIsValid){
				return '#FF7A30';
			}
			else{
				return '#9CA1AB';
			}
		},
		clickSubmit(){
			const vcSelf = this;
			vcSelf.$pubsub.modules.configuratorModal.pub('submitClicked', {
				pageKey: vcSelf.selectedTab,
			});
			this.notifiyTabSaved(vcSelf.selectedTab);

			if(vcSelf.currentTabFlow){
				// move on to the next tab
				if(vcSelf.currentTabIndex === vcSelf.sortedSheetViewConfigurationTabs.length-1){
					// last step, close the modal
					vcSelf.$emit('closeModal');
					vcSelf.sheetView.setSheetConfigured();
				}
				else{
					vcSelf.selectedTab = vcSelf.sortedSheetViewConfigurationTabs[vcSelf.currentTabIndex+1].tabKey;
				}
			}
			else{
				// not in a tab-flow, if they click done, theyre done with everything
				vcSelf.$emit('closeModal');
				vcSelf.sheetView.setSheetConfigured();
			}
			return;
		},
	},
}
</script>

<style scoped lang="scss">

.configurator-container-body{
	height: 100%;

	.configurator-x-button-container{
		position: absolute;
		right: 18px;
		top: 18px;
		z-index: 1;

		.configurator-x-button{
			cursor: pointer;
		}
	}

	.configurator-grid{
		@include flex(row, flex-start, flex-start);
		height: 100%;

		.configurator-left-column{
			@include flex(column, flex-start, flex-start);
			height: 100%;
			flex-grow: 1;

			.configurator-title{
				font-family: $rvFontFamily;
				font-size: 18px;
				line-height: 28px;
				text-align: left;
				letter-spacing: -0.015em;
				font-feature-settings: 'liga' off;
				color: $p-color-grey-600;
				margin: 18px;
				cursor: default;
				user-select: none;

				display: grid;
				grid-template-columns: 1fr 25px 1fr;
				width: max-content;

				.title-sheet-type{

				}
				.title-divider{
					position: relative;
					display: block;
					width: 0px;
					height: 28px;
					border-right: 1px solid $p-color-grey-300;
					margin: 0 12px;
				}
				.title-setup{

				}
			}

			.configurator-tab-container{
				@include flex(row, flex-start, center);

				width: 100%;

				.configurator-tab{
					@include flex(row, center, center);

					border-bottom: 2px solid transparent;
					margin-left: 18px;
					cursor: pointer;
					user-select: none;
					color: $p-color-grey-400;
					padding-bottom: 4px;
					transition: color $p-transition-medium, border-bottom $p-transition-medium;

					.configurator-tab-icon{

					}

					.configurator-tab-text{
						font-family: $rvFontFamily;
						font-size: 14px;
						line-height: 20px;
						margin-left: 6px;
					}

					&.warning{
						color: $p-color-orange-300;

						&:hover{
							color: $p-color-orange-300;
							border-bottom: 2px solid $p-color-orange-300;
						}
					}

					&.selected{
						color: $p-color-blue-500;
						border-bottom: 2px solid $p-color-blue-300;

						&:hover{
							color: $p-color-blue-500;
							border-bottom: 2px solid $p-color-blue-300;
						}
					}

					&.disabled{
						color: $p-color-grey-400;
						cursor: default;

						&:hover{
							color: $p-color-grey-400;
							border-bottom: 2px solid transparent;
						}
					}

					&:hover{
						color: $p-color-grey-400;
						border-bottom: 2px solid $p-color-grey-400;
					}
				}
			}

			.configurator-component-container{
				display: flex;
				justify-content: center;
				width: 100%;
				height: 466px;
				overflow-y: auto;
			}

			.configurator-footer{
				@include flex(row, space-between);

				position: relative;
				bottom: 0px;
				border-radius: 0px 0px 3px 3px;
				background-color: $p-color-white;
				width: 100%;
				height: 80px;
				padding: 0 10px 0 18px;

				.configurator-objects-status-container{
					@include flex(row, center, center);
					cursor: pointer;

					.configurator-objects-status-icon{
						color: $p-color-blue-300;

						&.warning{
							color: $p-color-orange-300;
						}

						&.deactivated{
							color: $p-color-grey-400;
						}
					}

					.configurator-objects-status-text{
						font-family: $rvFontFamily;
						font-weight: 600;
						font-size: 14px;
						line-height: 20px;
						letter-spacing: 0.15px;
						color: $p-color-blue-300;
						margin-left: 9px;

						&.warning{
							color: $p-color-orange-300;
						}

						&.deactivated{
							color: $p-color-grey-400;
						}
					}
				}

				.configurator-objects-status-content{
					width: 382px;
					padding: 24px 9px 9px 9px;
					
					.objects-status-title{
						font-family: $rvFontFamily;
						font-weight: 600;
						font-size: 14px;
						line-height: 20px;
						letter-spacing: 0.15px;
						color: $p-color-blue-300;
						margin-left: 9px;
						margin-bottom: 18px;

						&.warning{
							color: $p-color-orange-300;
						}
					}

					.objects-status-body{
						padding-right: 9px;

						.objects-status-group{
							@include flex(row, flex-start, flex-start);
							padding: 9px 0;

							.objects-status-group-icon{
								margin-left: 4px;
								color: $p-color-grey-400;
							}

							.objects-status-group-content{
								margin-left: 6px;
								cursor: default;

								.objects-status-group-content-header{
									font-family: $rvFontFamily;
									font-size: 14px;
									line-height: 20px;
									letter-spacing: 0.15px;
									color: $p-color-grey-600;
									margin-bottom: 12px;
								}

								.objects-status-group-content-detail{
									font-family: $rvFontFamily;
									font-size: 13px;
									line-height: 20px;
									letter-spacing: 0.15px;
									color: $p-color-grey-600;
									margin-left: 12px;
									margin-bottom: 9px;
									display: inline-block;

									.status-group-content-field-info{

										&.fullwidth{
											width: 330px;
										}
									}
								}

								.status-group-content-field-label{
									display: inline-flex;
									position: relative;
									top: 5px;

									&.add-margin{
										margin-right: 2px;
									}
								}

								.group-content-info-container{
									@include flex(row, center, center);
									background-color: $p-color-grey-100;
									border-radius: 3px;
									padding: 12px 18px 12px 9px;
									margin-top: 12px;

									.group-content-info-icon{
										color: $p-color-grey-400;
										margin-right: 12px;
									}
									.group-content-info-text{
										font-family: $rvFontFamily;
										font-weight: 600;
										font-size: 13px;
										line-height: 19px;
										letter-spacing: 0.15px;
										color: $p-color-grey-500;
									}
								}

							}

						}
					}

				}

				.configurator-submit-button{

				}
			}
		}

		.configurator-right-bar{
			height: 642px;
			width: 240px;
			box-sizing: border-box;
			position: relative;
			top: 0px;
			right: 0px;
			padding: 64px 18px 0 18px;
			background-color: $p-color-grey-100;
			box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
			user-select: none;
			cursor: default;

			&.tab-flow{
				padding: 18px 18px 0 18px;
			}

			.preview-container{
				@include flex(column, center, center);
				margin-bottom: 50px;

				.preview-header{
					font-style: normal;
					font-weight: 600;
					font-size: 14px;
					line-height: 20px;
					text-align: left;
					letter-spacing: 0.01em;
					font-feature-settings: 'liga' off;
					color: $p-color-blue-500;
					margin-bottom: 24px;
				}
				.preview-image{

				}
				.preview-feature-conatiner{
					width: 100%;
					@include flex(column, center, flex-start);

					.feature-item{
						display: grid;
						grid-template-columns: 20px 1fr;
						align-items: end;
						grid-column-gap: 6px;

						.feature-check{

						}

						.feature-text{
							font-family: $rvFontFamily;
							font-style: normal;
							font-weight: normal;
							font-size: 12px;
							line-height: 18px;
							letter-spacing: 0.01em;
							font-feature-settings: 'liga' off;
							color: $p-color-teal-300;
						}
					}
				}
			}
			.testimonial-container{
				@include flex(column, center, center);

				.testimonial-header{
					font-style: normal;
					font-weight: 600;
					font-size: 14px;
					line-height: 20px;
					text-align: left;
					letter-spacing: 0.01em;
					font-feature-settings: 'liga' off;
					color: $p-color-blue-500;
					margin-bottom: 18px;
				}
				.testimonial-quote{
					padding: 12px;
					font-style: normal;
					font-weight: 500;
					font-size: 13px;
					line-height: 18px;
					letter-spacing: 0.3px;
					color: $p-color-white;
					background-color: $p-color-blue-500;
					position: relative;
					margin-bottom: 12px;

					&:after {
						content: "";
						position: absolute;
						top: 100%;
						left: 15%;
						margin-left: -5px;
						border-width: 5px;
						border-style: solid;
						border-color: $p-color-blue-500 transparent transparent transparent;
					}
				}
				.testimonial-attribution{
					font-style: normal;
					font-weight: normal;
					font-size: 14px;
					line-height: 20px;
					text-align: center;
					letter-spacing: 0.01em;
					font-feature-settings: 'liga' off;

					.attribution-title{
						color: $p-color-teal-300;
					}
					.attribution-company{
						color: $p-color-grey-400;
					}
				}
			}
		}
	}
}

</style>
